import type {
  GenerateFlowField,
  GenerateFlowFieldType,
  GenerateFlowInputStep,
} from "../data/generateFlowInputFields";
import type { FieldStep } from "../types/gameRuleKbGenerate";
import { fieldValueKey, getFieldValue } from "./generateFlowInputValidation";

const mapApiFieldType = (type: string): GenerateFlowFieldType => {
  if (type === "select") return "select";
  if (type === "number") return "number";
  if (type === "date") return "date";
  return "text";
};

export const orderFieldStepsBySelection = (
  steps: readonly FieldStep[],
  selectedSectionIds: readonly string[],
): readonly FieldStep[] => {
  const programme = steps.find((step) => step.id === "programme");
  const byId = new Map(
    steps.filter((step) => step.id !== "programme").map((step) => [step.id, step]),
  );
  const orderedSections = selectedSectionIds
    .map((id) => byId.get(id))
    .filter((step): step is FieldStep => step !== undefined);
  return programme ? [programme, ...orderedSections] : orderedSections;
};

/** KB section steps only — excludes backend "programme" (filled from Step 1). */
export const filterKbSectionInputSteps = (
  steps: readonly GenerateFlowInputStep[],
): readonly GenerateFlowInputStep[] =>
  steps.filter((step) => step.id !== "programme");

export const mapApiFieldStepsToInputSteps = (
  steps: readonly FieldStep[],
): readonly GenerateFlowInputStep[] =>
  steps.map((step) => ({
    id: step.id,
    name: step.name,
    tag: step.tag,
    description: step.description,
    content: step.content,
    fields: step.fields.map(
      (field): GenerateFlowField => ({
        key: field.key,
        label: field.label,
        type: mapApiFieldType(field.type),
        required: field.required,
        value: field.value,
        options: field.dropdownOptions ?? undefined,
        docPlaceholder: field.docPlaceholder,
        hint: field.hint,
      }),
    ),
  }));

/** Programme metadata for session PATCH — sourced from Step 1, not Step 3 UI. */
export const buildProgrammeFromStep1 = (prefill: {
  readonly gameRuleName: string;
  readonly sponsorName?: string;
  readonly administratorName?: string;
  readonly savedProgramme?: Record<string, string> | null;
}): Record<string, string> => {
  const saved = prefill.savedProgramme ?? {};
  return {
    ruleName: (prefill.gameRuleName.trim() || saved.ruleName || "").trim(),
    sponsor: (prefill.sponsorName?.trim() || saved.sponsor || "").trim(),
    admin: (prefill.administratorName?.trim() || saved.admin || "").trim(),
  };
};

export const buildFieldValuesPayload = (
  inputSteps: readonly GenerateFlowInputStep[],
  values: Readonly<Record<string, string>>,
): Record<string, Record<string, string>> => {
  const payload: Record<string, Record<string, string>> = {};
  for (const step of inputSteps) {
    if (step.id === "programme") {
      continue;
    }
    const sectionValues: Record<string, string> = {};
    for (const field of step.fields) {
      sectionValues[field.key] = getFieldValue(step.id, field, values).trim();
    }
    payload[step.id] = sectionValues;
  }
  return payload;
};

type InitializeFieldValuesPrefill = {
  readonly savedFieldValues?: Record<string, unknown> | null;
  readonly useApiDefaultsOnly?: boolean;
};

export const initializeInputFieldValuesFromSteps = (
  steps: readonly GenerateFlowInputStep[],
  prev: Readonly<Record<string, string>>,
  prefill: InitializeFieldValuesPrefill = {},
): Record<string, string> => {
  const next: Record<string, string> = {};
  const useApiDefaultsOnly = prefill.useApiDefaultsOnly ?? false;
  const savedFieldValues = useApiDefaultsOnly
    ? {}
    : (prefill.savedFieldValues ?? {});

  for (const inputStep of steps) {
    if (inputStep.id === "programme") {
      continue;
    }

    const savedSection = savedFieldValues[inputStep.id] as
      | Record<string, unknown>
      | undefined;

    for (const field of inputStep.fields) {
      const key = fieldValueKey(inputStep.id, field.key);
      if (useApiDefaultsOnly) {
        next[key] = field.value ?? "";
        continue;
      }
      const savedRaw =
        savedSection && typeof savedSection === "object"
          ? savedSection[field.key]
          : undefined;
      const savedValue = typeof savedRaw === "string" ? savedRaw : "";
      next[key] = prev[key] ?? savedValue ?? field.value ?? "";
    }
  }

  return next;
};
